import { useState } from "react";
import { FarmaClassLogo } from "@/components/FarmaClassLogo";
import { BookingBanner } from "@/components/BookingBanner";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { ArrowRight, Sparkles, ShieldCheck, Lock, Phone, Mail as MailIcon } from "lucide-react";
import { toast } from "sonner";
import { RequestInfoModal } from "@/components/RequestInfoModal";

export default function LoginPage() {
  const { user, login, loading } = useAuth();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [blockedInfo, setBlockedInfo] = useState(null); // {reason?} → si presente, se pinta el modal
  const [infoOpen, setInfoOpen] = useState(false);
  const navigate = useNavigate();

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await login(identifier, password);
      toast.success("Bienvenido");
      navigate("/");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Bloqueo manual por el admin → pantalla modal específica en vez de toast
      if (detail && typeof detail === "object" && detail.error_code === "user_blocked") {
        setBlockedInfo({ reason: detail.blocked_reason || "" });
      } else {
        toast.error(typeof detail === "string" ? detail : "Error al iniciar sesión");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left: brand panel */}
      <div className="hidden lg:flex relative bg-slate-950 text-white p-12 flex-col justify-between overflow-hidden">
        {/* Intro video (background) */}
        <video
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          poster="/archiduque-logo.jpg"
          data-testid="login-intro-video"
          className="absolute inset-0 w-full h-full object-cover opacity-70"
        >
          <source src="/intro-farmaclass.mp4" type="video/mp4" />
        </video>
        {/* Dark gradient overlay for legibility */}
        <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/60 to-slate-950/85" />
        <div className="absolute inset-0 bg-grid opacity-20" />
        <div className="relative">
          <FarmaClassLogo variant="white" className="h-16" testId="login-farmaclass-logo" />
        </div>
        <div className="relative space-y-8">
          <h1 className="font-display text-5xl xl:text-6xl font-bold leading-[0.95] tracking-tighter">
            Clasifica la dispensación EFG de tu farmacia.
          </h1>
          <p className="text-slate-300 text-lg max-w-md leading-relaxed">
            Sube un Excel o TXT con códigos nacionales y unidades. Obtén separación marca vs. genérico, semaforización por dispensación y desglose por laboratorio.
          </p>
          <div className="flex flex-wrap gap-3 pt-4">
            <Tag>Excel · TXT · CSV</Tag>
            <Tag>CN &gt; 600.000</Tag>
            <Tag>Semáforo Verde · Amarillo · Rojo</Tag>
          </div>
        </div>
        <div className="relative flex items-center justify-between gap-4 text-xs uppercase tracking-[0.2em] text-slate-400">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" strokeWidth={1.5} />
            Datos cifrados y privados de la farmacia
          </div>
          <div className="flex items-center gap-2 pl-4 border-l border-white/10" data-testid="archiduque-brand">
            <span className="text-[10px] tracking-[0.18em] text-slate-500">Powered by</span>
            <div className="bg-white rounded-md px-3 py-2 flex items-center">
              <img
                src="/archiduque-logo.jpg"
                alt="Archiduque Consulting"
                className="h-14 w-auto object-contain"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-4 sm:p-8 lg:p-12 bg-slate-50">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8">
            <FarmaClassLogo className="h-14" testId="login-mobile-farmaclass-logo" />
          </div>

          <BookingBanner />

          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold mb-3">Acceso · Farmacia</div>
          <h2 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-950 mb-2">
            Inicia sesión
          </h2>
          <p className="text-slate-500 mb-6">Introduce las credenciales de tu farmacia.</p>

          {/* Banner para visitantes que aún no son clientes */}
          <div
            className="lead-glow relative overflow-hidden mb-6 rounded-2xl bg-gradient-to-br from-slate-900 via-slate-900 to-sky-900 text-white p-5 shadow-xl"
            data-testid="login-lead-banner"
          >
            <span className="booking-shine pointer-events-none absolute inset-y-0 -left-1/2 w-1/2 [animation-delay:1.4s]" aria-hidden="true" />
            <div className="relative text-xs uppercase tracking-[0.2em] font-extrabold text-sky-300">
              ¿Todavía no eres cliente?
            </div>
            <p className="relative font-display text-xl sm:text-2xl font-bold leading-tight mt-2">
              Descubre cuánto puede mejorar tu cuota de genérico.
            </p>
            <p className="relative text-sm text-slate-300 mt-1.5">Información sin compromiso.</p>
            <a
              href="/informacion"
              className="relative mt-4 flex items-center justify-center gap-2 w-full bg-sky-400 hover:bg-sky-300 text-slate-950 px-5 py-3.5 rounded-xl text-lg font-extrabold transition-[background-color,transform] lead-cta"
              data-testid="login-landing-link"
            >
              <Sparkles className="h-5 w-5" strokeWidth={2.4} /> Ver qué hace FarmaClass <ArrowRight className="h-5 w-5" strokeWidth={2.6} />
            </a>
            <button
              type="button"
              onClick={() => setInfoOpen(true)}
              className="relative mt-2 flex items-center justify-center gap-1.5 w-full border border-white/30 hover:bg-white/10 text-white px-4 py-2.5 rounded-xl text-sm font-bold transition-colors"
              data-testid="login-request-info-btn"
            >
              Solicitar información <ArrowRight className="h-4 w-4" strokeWidth={2.2} />
            </button>
          </div>

          {/* Soporte / contacto para pedir claves */}
          <div
            className="mb-8 rounded-md border border-slate-200 bg-white px-4 py-3 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm"
            data-testid="login-support-block"
          >
            <span className="text-[10px] uppercase tracking-[0.18em] font-semibold text-slate-500">
              ¿Necesitas tus claves?
            </span>
            <a
              href="tel:+34609467788"
              className="font-mono-num text-slate-900 hover:text-slate-600 transition-colors"
              data-testid="login-support-phone"
            >
              609 46 77 88
            </a>
            <a
              href="mailto:pedro@archiduquecarlos.com"
              className="text-slate-900 hover:text-slate-600 transition-colors"
              data-testid="login-support-email"
            >
              pedro@archiduquecarlos.com
            </a>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2 block">
                Código de licencia o email
              </label>
              <input
                data-testid="login-email-input"
                type="text"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                placeholder="FC-2026-0001 ó tu@email.com"
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                required
              />
            </div>
            <div>
              <label className="text-xs uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2 block">
                Contraseña
              </label>
              <input
                data-testid="login-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-transparent transition-all"
                required
              />
            </div>
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={submitting}
              className="w-full bg-slate-950 text-white py-3.5 rounded-md font-medium hover:bg-slate-800 transition-all duration-200 flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {submitting ? "Entrando…" : "Entrar"}
              <ArrowRight className="h-4 w-4" strokeWidth={2} />
            </button>
          </form>

          <p className="text-xs text-slate-400 mt-10 leading-relaxed">
            Al iniciar sesión aceptas el tratamiento de los datos de dispensación según la política de privacidad de la farmacia.
          </p>
        </div>
      </div>

      {infoOpen && <RequestInfoModal onClose={() => setInfoOpen(false)} />}

      {blockedInfo && (
        <div
          className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-4"
          data-testid="user-blocked-modal"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-md w-full p-8 sm:p-10 relative">
            <div className="mx-auto h-14 w-14 rounded-full bg-rose-100 grid place-items-center mb-5">
              <Lock className="h-7 w-7 text-rose-600" strokeWidth={1.8} />
            </div>
            <h3 className="font-display text-2xl font-bold text-slate-950 tracking-tight text-center mb-2">
              Su suscripción ha caducado
            </h3>
            <p className="text-slate-600 text-center leading-relaxed mb-1">
              No te pierdas todas las mejoras que la herramienta tiene para ti y todo el beneficio que puedes obtener.
            </p>
            <p className="text-slate-800 text-center font-semibold mb-6">
              Reactiva tu suscripción hablando con el administrador.
            </p>

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-2">
              <a
                href="tel:+34609467788"
                className="flex items-center gap-3 text-slate-900 hover:text-emerald-700 transition-colors"
                data-testid="blocked-modal-phone"
              >
                <Phone className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
                <span className="font-mono-num font-semibold">609 46 77 88</span>
              </a>
              <a
                href="mailto:pedro@archiduquecarlos.com"
                className="flex items-center gap-3 text-slate-900 hover:text-emerald-700 transition-colors break-all"
                data-testid="blocked-modal-email"
              >
                <MailIcon className="h-4 w-4 text-emerald-600" strokeWidth={1.8} />
                <span className="font-semibold">pedro@archiduquecarlos.com</span>
              </a>
            </div>

            <button
              onClick={() => setBlockedInfo(null)}
              className="mt-6 w-full bg-slate-900 hover:bg-slate-800 text-white py-3 rounded-md font-medium transition-colors"
              data-testid="blocked-modal-close"
            >
              Entendido
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

const Tag = ({ children }) => (
  <span className="text-xs uppercase tracking-[0.2em] font-semibold px-3 py-1.5 rounded-full bg-white/10 border border-white/20 text-slate-100">
    {children}
  </span>
);
